import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DragHandle from 'blocks/Timeline/components/TrackAsset/DragHandle';
import {
  debouncedSaveConfiguration,
  startAssetEdit,
  stopAssetEdit,
} from 'redux/modules/embed/actions';
import { adjustLayoutBoundary } from 'redux/modules/embed/actions/layout';
import { useTrackAssetContext } from '../../components/TrackAsset';
import { block } from '../../utils';
import { FRAMING_HANDLE_WIDTH_PX } from './constants';
import { useFramingTimelineContext } from './FramingTimelineContext';
import {
  createDragHandleBoundsSelector,
  createLayoutTimelineHandlePositionMillisSelector,
} from './selectors';

export type FramingTimelineHandleProps = {
  /**
   * 0-based index indicating where the handle falls.  For example, 0 is the left
   * handle of the first element, 1 is the handle between the first and second element,
   * etc.
   */
  gapIndex: number;
  onDragStart?: (gapIndex: number) => void;
  onDragStop?: (gapIndex: number) => void;
};

const FramingTimelineHandle = ({
  gapIndex,
  onDragStart,
  onDragStop,
}: FramingTimelineHandleProps) => {
  const dispatch = useDispatch();

  const { id: videoId, pxPerSec } = useTrackAssetContext();

  const {
    convertAssetTimeToTimelinePosition,
    convertFramingPositionToAssetTime,
    convertAssetTimeToFramingPosition,
  } = useFramingTimelineContext();

  const positionMillis = useSelector(
    createLayoutTimelineHandlePositionMillisSelector(videoId, gapIndex),
  );

  const [dragging, setDragging] = useState(false);

  const { minMillis, maxMillis } = useSelector(
    createDragHandleBoundsSelector(videoId, gapIndex),
  );

  const handleDragStart = useCallback(() => {
    dispatch(startAssetEdit(videoId, 'move'));
    setDragging(true);
    onDragStart?.(gapIndex);
  }, [dispatch, gapIndex, onDragStart, videoId]);

  const handleDragStop = useCallback(() => {
    dispatch(stopAssetEdit());
    setDragging(false);
    onDragStop?.(gapIndex);
    dispatch(debouncedSaveConfiguration());
  }, [dispatch, gapIndex, onDragStop]);

  const handleDrag = useCallback(
    data => {
      dispatch(
        adjustLayoutBoundary(
          videoId,
          gapIndex,
          convertFramingPositionToAssetTime(data.x),
        ),
      );
    },
    [convertFramingPositionToAssetTime, dispatch, gapIndex, videoId],
  );

  return (
    <DragHandle
      className={block('autoframed-video-asset-framing-timeline-handle', {
        active: dragging,
      })}
      bounds={{
        left: convertAssetTimeToFramingPosition(minMillis),
        right:
          convertAssetTimeToFramingPosition(maxMillis) +
          FRAMING_HANDLE_WIDTH_PX,
      }}
      displayX={convertAssetTimeToTimelinePosition(positionMillis)}
      id={`handle-${gapIndex}`}
      onStart={handleDragStart}
      onStop={handleDragStop}
      onDrag={handleDrag}
      pxPerSec={pxPerSec}
      showTooltip={dragging}
      style={{
        ['--framing-handle-width' as string]: `${FRAMING_HANDLE_WIDTH_PX}px`,
      }}
      x={convertAssetTimeToFramingPosition(positionMillis)}
      width={10}
    />
  );
};

export default FramingTimelineHandle;
